// Global Instructions Rule Applied!
//
// Pure transform shared by the desktop Commitments tab (Ant Table tree data) and
// the mobile Commitments summary (Ant List). Turns the flat fund_commitment rows
// from view__account_commitments_tablegrid into fund-grouped display rows.
//
// Grouping rule (Fund -> commitments):
//  - a fund with a SINGLE commitment renders as one inline row (kind 'fund-single')
//    carrying the full commitment detail (entity, status, dates, fees, amount);
//  - a fund with MORE THAN ONE commitment renders as a parent row (kind 'fund')
//    whose per-commitment rows are its `children` (kind 'commitment'); the parent
//    is expandable. This covers both multiple entities AND one entity committing
//    more than once.
//  - the investing-entity label on a parent fund row is the entity name when the
//    fund has exactly one distinct investing entity, else "N entities".
//
// Total rows from the RPC (carrying `_total_label`) are ignored here — the caller
// renders the per-currency grand total(s) separately.

export type CommitmentRow = Record<string, any>

export interface FundGroupRow extends CommitmentRow {
  key: string
  kind: 'fund' | 'fund-single' | 'commitment'
  funds__id?: string | null
  funds__name?: string | null
  /** Shown in the entity column of a parent ('fund') row. */
  entity_label?: string
  /** Distinct investing entities / commitment count in the fund. */
  entity_count?: number
  commitment_count?: number
  /** Per-fund total: raw (for compact mobile formatting) + full formatted string. */
  fund_total_amount?: number
  currency_code?: string | null
  currency_symbol?: string | null
  children?: FundGroupRow[]
}

const AMOUNT_KEY = 'fund_commitments__amount'

/** Full amount with its currency symbol, no decimals — mirrors the RPC's p_decimals=0 totals. */
function formatFullAmount(symbol: string | null | undefined, amount: number): string {
  const grouped = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(amount))
  return `${symbol ?? ''}${grouped}`
}

export function groupCommitmentsByFund(rows: CommitmentRow[]): FundGroupRow[] {
  const groups = new Map<string, CommitmentRow[]>()
  for (const r of rows) {
    if (!r || r._total_label) continue
    const fundId = String(r.funds__id ?? r.funds__name ?? 'unknown')
    const existing = groups.get(fundId)
    if (existing) existing.push(r)
    else groups.set(fundId, [r])
  }

  const out: FundGroupRow[] = []
  for (const [fundId, commitments] of groups) {
    const first = commitments[0] || {}
    const symbol = (first.currency_symbol as string | null) ?? null
    const total = commitments.reduce((sum, c) => sum + (Number(c[AMOUNT_KEY]) || 0), 0)
    const distinctEntities = new Set(commitments.map((c) => String(c.investors__id ?? c.investors__name ?? '')))
    const entityCount = distinctEntities.size

    // Single commitment -> one inline row carrying the full commitment detail.
    if (commitments.length === 1) {
      const c = commitments[0]
      out.push({
        ...c,
        key: `fund-single-${String(c.fund_commitments__id ?? fundId)}`,
        kind: 'fund-single',
        funds__id: c.funds__id ?? fundId,
        funds__name: c.funds__name ?? null,
        entity_count: entityCount,
        commitment_count: 1,
        fund_total_amount: total
      })
      continue
    }

    // Multiple commitments -> expandable parent with per-commitment children.
    const children: FundGroupRow[] = commitments.map((c, i) => ({
      ...c,
      key: `commitment-${String(c.fund_commitments__id ?? `${fundId}-${i}`)}`,
      kind: 'commitment'
    }))

    out.push({
      key: `fund-${fundId}`,
      kind: 'fund',
      funds__id: first.funds__id ?? fundId,
      funds__name: first.funds__name ?? null,
      entity_label: entityCount === 1 ? String(first.investors__name ?? '-') : `${entityCount} entities`,
      entity_count: entityCount,
      commitment_count: commitments.length,
      fund_total_amount: total,
      fund_commitments__amount_fmt: formatFullAmount(symbol, total),
      currency_code: (first.currency_code as string | null) ?? null,
      currency_symbol: symbol,
      children
    })
  }

  return out
}
